const byStart = (a, b) => a.startDay - b.startDay || a.endDay - b.endDay || (a.key < b.key ? -1 : a.key > b.key ? 1 : 0);

/** Index of the first entry of an ascending array that is not below `value`. */
function lowerBound(values, value) {
  let low = 0;
  let high = values.length;
  while (low < high) {
    const middle = (low + high) >>> 1;
    if (values[middle] < value) low = middle + 1;
    else high = middle;
  }
  return low;
}

/**
 * Stacks bars into lanes so that bars sharing a lane never overlap. Every bar is an item `{ key, startDay, endDay }`.
 *
 * Built from scratch, items are placed in start order, each in the first lane that is free for its whole range,
 * which uses as few lanes as the busiest day needs. After that the layout is sticky: items that arrive later are
 * placed around the existing ones and no item changes lane, so bars do not jump while more data is loaded.
 * Only a removal or a change of dates, which is an edit and not a load, lays everything out again.
 *
 * Each lane keeps its items in order of their start, and since they do not overlap, in order of their end too,
 * so what overlaps a range is found by a binary search.
 */
export class LaneLayout {
  constructor() {
    this._lanes = [];
    this._placed = new Map();
  }

  static build(items) {
    const layout = new LaneLayout();
    layout._placeAll(items);
    return layout;
  }

  get laneCount() {
    return this._lanes.length;
  }

  get size() {
    return this._placed.size;
  }

  has(key) {
    return this._placed.has(key);
  }

  /** The lane an item is in, or -1 when it is not part of the layout. */
  laneOf(key) {
    return this._placed.has(key) ? this._placed.get(key).lane : -1;
  }

  /** Puts an item in the first lane that is free for its range, unless it is already placed. Returns its lane. */
  place(item) {
    const existing = this._placed.get(item.key);
    if (existing) return existing.lane;

    let lane = this._lanes.findIndex((candidate) => !LaneLayout._overlaps(candidate, item.startDay, item.endDay));
    if (lane === -1) {
      lane = this._lanes.length;
      this._lanes.push({ starts: [], ends: [], keys: [] });
    }

    const target = this._lanes[lane];
    const index = lowerBound(target.ends, item.startDay);
    target.starts.splice(index, 0, item.startDay);
    target.ends.splice(index, 0, item.endDay);
    target.keys.splice(index, 0, item.key);
    this._placed.set(item.key, { lane, startDay: item.startDay, endDay: item.endDay });

    return lane;
  }

  /**
   * Makes the layout hold exactly `items`. Items that are only added are placed without moving any other. When one
   * was removed or changed its dates the whole layout is made again from the items.
   */
  sync(items) {
    const incoming = new Map(items.map((item) => [item.key, item]));

    for (const [key, placed] of this._placed) {
      const item = incoming.get(key);
      if (!item || item.startDay !== placed.startDay || item.endDay !== placed.endDay) {
        this._lanes = [];
        this._placed = new Map();
        this._placeAll(items);
        return;
      }
    }

    this._placeAll(items.filter((item) => !this._placed.has(item.key)));
  }

  /**
   * Calls `visit(key, lane, startDay, endDay)` for every item that overlaps the closed range of days. Only the lanes
   * from `firstLane` to `lastLane` are looked at, for a row that shows just a few of its lanes at a time.
   */
  forEachInRange(firstDay, lastDay, visit, firstLane = 0, lastLane = Infinity) {
    const end = Math.min(this._lanes.length - 1, lastLane);

    for (let lane = Math.max(0, firstLane); lane <= end; lane += 1) {
      const { starts, ends, keys } = this._lanes[lane];
      for (let index = lowerBound(ends, firstDay); index < starts.length && starts[index] <= lastDay; index += 1) {
        visit(keys[index], lane, starts[index], ends[index]);
      }
    }
  }

  _placeAll(items) {
    for (const item of [...items].sort(byStart)) this.place(item);
  }

  static _overlaps(lane, startDay, endDay) {
    const index = lowerBound(lane.ends, startDay);
    return index < lane.starts.length && lane.starts[index] <= endDay;
  }
}
